// The chip-in flow (modal body): pick an accepted stablecoin, request a fresh deposit
// address, show address + QR + countdown. Universal path needs no wallet; the one-click
// path (progressive enhancement) appears only when an injected EIP-1193 provider exists.
// No viem/wagmi — the single ERC-20 transfer is hand-rolled in ../../commons/erc20.
import { button } from "../../design/components.js";
import { qrSvg } from "../../platform/qr.js";
import { chainName, railPrivacyKey, type TokenCfg } from "../../commons/tokens.js";
import { sendErc20Transfer, toBaseUnits, type Eip1193 } from "../../commons/erc20.js";

export type ChipInCtx = {
  t: (k: string, p?: Record<string, string | number>) => string;
  tokens: TokenCfg[];
  rail: string;
  beneficiary: string;
  requestAddress: (rail: string) => Promise<{ address: string; expiresAt: string; windowSeconds: number }>;
  provider?: Eip1193 | null;
  /** Closes the modal — wires the bottom "Done" button. */
  onDone?: () => void;
  /** Subscribe to "my deposit landed on the firehose" (the contribution watcher
   *  publishes ~10s after it sees the deposit on-chain). Calls onReceived once and
   *  returns an unsubscribe. When provided, the flow shows a live waiting spinner
   *  that flips to a thank-you; when absent it falls back to static guidance. */
  watchReceipt?: (onReceived: () => void) => () => void;
};

function elx(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

export function chipInContent(ctx: ChipInCtx): HTMLElement {
  const { t } = ctx;
  const root = elx("div", "commons-chipin");

  // What this is — a one-off gift, detected on-chain, no sign-up.
  root.append(elx("p", "commons-chipin__intro bx-body", t("commons.chipIn.intro")));

  // Token picker.
  root.append(elx("div", "bx-microlabel", t("commons.chipIn.pickToken")));
  const picker = document.createElement("select");
  picker.className = "commons-chipin__token bx-input";
  for (const tok of ctx.tokens) {
    const opt = document.createElement("option");
    opt.value = tok.symbol;
    opt.textContent = tok.symbol;
    picker.append(opt);
  }
  root.append(picker);

  // Network of the selected token + a wrong-chain warning. Funds sent on another
  // chain reach an identical-looking address but are never detected.
  const net = elx("p", "commons-chipin__network bx-microlabel");
  const syncNet = () => {
    const tok = ctx.tokens.find((x) => x.symbol === picker.value) ?? ctx.tokens[0];
    net.textContent = tok ? t("commons.chipIn.network", { network: chainName(tok.chainId) }) : "";
  };
  picker.addEventListener("change", syncNet);
  syncNet();
  root.append(net);
  root.append(elx("p", "commons-chipin__warn bx-microlabel", t("commons.chipIn.wrongChain")));

  // Honest per-rail privacy class.
  root.append(elx("p", "commons-chipin__privacy bx-microlabel", t(railPrivacyKey(ctx.rail))));

  // Optional amount (powers the one-click path; the universal path needs no amount).
  root.append(elx("label", "bx-microlabel", t("commons.chipIn.amount")));
  const amount = document.createElement("input");
  amount.type = "text";
  amount.inputMode = "decimal";
  amount.className = "commons-chipin__amount bx-input";
  root.append(amount);

  // Address request + result area. A live receipt subscription is held here so a
  // re-request (or Done) can tear it down before opening a new one.
  let receiptUnsub: (() => void) | null = null;
  const clearReceipt = (): void => { receiptUnsub?.(); receiptUnsub = null; };
  // Once the deposit is confirmed there's nothing left to do but leave, so the
  // dismiss button graduates from a quiet "Done" to a primary "Close" (assigned
  // when the button is built below; a no-op until then).
  let promoteDone: () => void = () => {};
  const result = elx("div", "commons-chipin__result");
  const get = button({ label: t("commons.chipIn.getAddress"), variant: "primary" });
  get.setAttribute("data-action", "commons-get-address");
  get.addEventListener("click", () => {
    clearReceipt();
    result.replaceChildren();
    void ctx.requestAddress(ctx.rail).then((res) => {
      receiptUnsub = renderAddress(ctx, result, res, () => picker.value, () => amount.value, () => promoteDone());
    }).catch(() => {
      result.replaceChildren(elx("p", "commons-chipin__error bx-body", t("commons.chipIn.error")));
    });
  });
  root.append(get, result);

  // Done — dismiss the flow (and stop watching). The explicit close is the natural
  // end-point once the supporter has sent (or decided not to).
  if (ctx.onDone) {
    const done = button({ label: t("commons.chipIn.done"), variant: "secondary", onClick: () => { clearReceipt(); ctx.onDone!(); } });
    done.setAttribute("data-action", "commons-done");
    promoteDone = () => {
      done.classList.remove("bx-btn--secondary");
      done.classList.add("bx-btn--primary");
      done.textContent = t("commons.chipIn.close");
    };
    root.append(done);
  }
  return root;
}

function renderAddress(
  ctx: ChipInCtx,
  host: HTMLElement,
  res: { address: string; expiresAt: string; windowSeconds: number },
  selectedSymbol: () => string,
  enteredAmount: () => string,
  onReceived: () => void,
): (() => void) | null {
  const { t } = ctx;
  host.replaceChildren();
  host.append(elx("div", "bx-microlabel", t("commons.chipIn.address")));

  const addr = elx("code", "commons-chipin__addr", res.address);
  host.append(addr);

  const qr = elx("div", "commons-chipin__qr");
  qr.innerHTML = qrSvg(res.address);
  host.append(qr);

  host.append(elx("p", "commons-chipin__deadline bx-microlabel",
    t("commons.chipIn.deadline", { min: Math.round(res.windowSeconds / 60) })));

  const copy = button({ label: t("commons.chipIn.copy"), variant: "secondary" });
  copy.setAttribute("data-action", "commons-copy");
  copy.addEventListener("click", () => {
    void navigator.clipboard?.writeText(res.address).catch(() => {});
    copy.textContent = t("commons.chipIn.copied");
  });
  host.append(copy);

  // One-click path: only when an injected provider exists.
  const provider = ctx.provider;
  if (provider) {
    const oneClick = button({ label: t("commons.chipIn.oneClick"), variant: "primary" });
    oneClick.setAttribute("data-action", "commons-one-click");
    oneClick.addEventListener("click", () => {
      const tok = ctx.tokens.find((x) => x.symbol === selectedSymbol());
      const amt = enteredAmount().trim();
      if (!tok || !amt) {
        host.append(elx("p", "commons-chipin__error bx-body", t("commons.chipIn.error")));
        return;
      }
      void (async () => {
        try {
          const accounts = (await provider.request({ method: "eth_requestAccounts" })) as string[];
          const from = accounts?.[0];
          if (!from) throw new Error("no account");
          await sendErc20Transfer(provider, tok.contract, res.address, toBaseUnits(amt, tok.decimals), from);
          oneClick.textContent = t("commons.chipIn.sent");
        } catch {
          host.append(elx("p", "commons-chipin__error bx-body", t("commons.chipIn.error")));
        }
      })();
    });
    host.append(oneClick);
  }

  // What happens next. With a live receipt feed we show a waiting spinner that
  // flips to a thank-you when the deposit lands on the firehose; without it we
  // fall back to static guidance (detection still happens, just not surfaced here).
  const after = elx("div", "commons-chipin__after");
  after.append(elx("div", "commons-chipin__after-title bx-microlabel", t("commons.chipIn.afterTitle")));

  let unsub: (() => void) | null = null;
  if (ctx.watchReceipt) {
    const status = elx("div", "commons-chipin__status");
    status.setAttribute("data-receipt-status", "waiting");
    const spinner = elx("span", "commons-chipin__spinner");
    spinner.setAttribute("aria-hidden", "true");
    const statusText = elx("span", "commons-chipin__status-text bx-body", t("commons.chipIn.waiting"));
    status.append(spinner, statusText);
    after.append(status, elx("p", "you-note bx-microlabel", t("commons.chipIn.afterAppear")));
    unsub = ctx.watchReceipt(() => {
      unsub?.(); // fire-once
      unsub = null;
      if (!host.isConnected) return; // modal already closed → toast covers it
      // The deposit landed — the address/QR have done their job. Replace the whole
      // result with a compact thank-you so the dialog shrinks to fit, and graduate
      // the dismiss button to a primary "Close".
      const success = elx("div", "commons-chipin__success");
      success.setAttribute("data-receipt-status", "received");
      const head = elx("div", "commons-chipin__status commons-chipin__status--done");
      head.append(
        elx("span", "commons-chipin__check", "✓"),
        elx("span", "commons-chipin__status-text bx-body", t("commons.chipIn.receivedTitle")),
      );
      success.append(head, elx("p", "commons-chipin__received bx-body", t("commons.chipIn.receivedBody")));
      host.replaceChildren(success);
      onReceived();
    });
  } else {
    after.append(elx("p", "bx-body", t("commons.chipIn.afterDetect")));
    after.append(elx("p", "bx-body", t("commons.chipIn.afterAppear")));
  }
  host.append(after);
  return unsub;
}
